import { Nullable } from '@tcc/common';
import {
  UapiAgronomicActivityType,
  UapiMachineDataMapName,
  UapiSeasonName,
} from '../../graphql/sdk';
import { getGraphqlClient } from '../uapiConnection';
import { swapComma } from '../../utils/swapComma';

/**
 * ARD (Analysis Ready Data) legend edition.
 *
 * New edit-legend path used when Ag-Viz is ON and the `dmb-ag-viz-legend-edition`
 * feature flag is ON. Instead of the legacy FVP `PUT legend/` request, edits are
 * sent to the UAPI machine-data pipeline through the `updateMachineDataMapLegend`
 * mutation.
 */

export const UpdateMachineDataMapLegendDocument = /* GraphQL */ `
  mutation UpdateMachineDataMapLegend($input: UpdateMachineDataMapLegendInput!) {
    updateMachineDataMapLegend(input: $input) {
      legend {
        units
        items {
          ... on ContinuousGeospatialAssetLegendItem {
            min
            max
            color {
              red
              green
              blue
              alpha
            }
          }
        }
      }
      errors {
        code
        reason
      }
    }
  }
`;

export interface UpdateMachineDataMapLegendInput {
  fieldId: string;
  cropId: string;
  seasonName: UapiSeasonName;
  seasonYear: number;
  activityType: UapiAgronomicActivityType;
  layer: UapiMachineDataMapName;
  start: number;
  end: number;
  numberOfBuckets: number;
}

export interface MutationError {
  code: string;
  reason: string;
}

interface UpdateMachineDataMapLegendPayload {
  legend: Nullable<{
    units: Nullable<string>;
    items: Array<{
      min: Nullable<number>;
      max: Nullable<number>;
      color: {
        red: number;
        green: number;
        blue: number;
        alpha: Nullable<number>;
      };
    }>;
  }>;
  errors: Nullable<MutationError[]>;
}

interface UpdateMachineDataMapLegendResponse {
  updateMachineDataMapLegend: UpdateMachineDataMapLegendPayload;
}

export function toNumericLegendValue(value: string | number | null | undefined): number {
  if (value === null || value === undefined) {
    return NaN;
  }

  const raw = String(value).trim();
  const normalized = Number.isNaN(Number(raw)) ? swapComma(raw) : raw;

  return Number(normalized);
}

/**
 * Send an Ag-Viz (ARD) legend edit to UAPI.
 *
 * @returns the mutation payload (updated legend + any application-level errors).
 */
export async function updateMachineDataMapLegendARD(
  input: UpdateMachineDataMapLegendInput
): Promise<UpdateMachineDataMapLegendPayload> {
  const client = getGraphqlClient();
  const data = await client.request<UpdateMachineDataMapLegendResponse>(
    UpdateMachineDataMapLegendDocument,
    { input }
  );

  return data.updateMachineDataMapLegend;
}
